import React, { ReactNode } from 'react';
import { DEFAULT_CATEGORY_ID, SELECTED_OPTIONS_CATEGORY_ID } from '../constants';
import { BaseOption, SelectableOption, SelectValueType } from '../types';

export interface UseCategorySelectionState<TValue extends SelectValueType = string> {
  /**
   * The options in the view.  Some may be not shown if using internal filtering.
   */
  options: BaseOption<TValue>[];
  /**
   * The selected options.
   * NOTE: this may include some items which are not in `state.options` if using
   * external filtering.
   */
  selectedOptions: Map<TValue, BaseOption<TValue>>;
}

export function getDefaultState<
  TValue extends SelectValueType = string,
>(): UseCategorySelectionState<TValue> {
  return { options: [], selectedOptions: new Map() };
}

/* -------------------------------------------------------------------------- */
/* Filters                                                                    */
/* -------------------------------------------------------------------------- */

export const matchFilter = (filter?: string, option?: BaseOption<any>) => {
  return filter && typeof option?.label === 'string'
    ? option?.label?.toLowerCase().includes(filter?.toLowerCase())
    : true;
};

export const matchCategory = (
  category?: string,
  option?: BaseOption<any>,
  groupSelectedInCategory = false,
) => {
  if (groupSelectedInCategory && option?.data?.isInSelectedCategory) {
    return category === SELECTED_OPTIONS_CATEGORY_ID;
  }
  return category && option ? option.categoryId === category : true;
};

export function matchCategoryAndFilter(
  category?: string,
  filter?: string,
  option?: BaseOption<any>,
  groupSelectedInCategory?: boolean,
) {
  const categoryMatch = matchCategory(category, option, groupSelectedInCategory);
  const filterMatch = matchFilter(filter, option);

  return categoryMatch && filterMatch;
}

type FilterFnProps = {
  isExternalSearchUsed?: boolean;
  filter?: string;
  category?: string;
  groupSelectedInCategory?: boolean;
};
/**
 * Returns the correct function for filtering depending on whether external
 * search is used
 *
 * REVIEW: Do we need to be matching by category if using external search?
 */
export const getFilterFunction = ({
  isExternalSearchUsed,
  filter,
  category,
  groupSelectedInCategory,
}: FilterFnProps) =>
  isExternalSearchUsed
    ? (option: BaseOption<any>) => matchCategory(category, option, groupSelectedInCategory)
    : (option: BaseOption<any>) =>
        matchCategoryAndFilter(category, filter, option, groupSelectedInCategory);

/* -------------------------------------------------------------------------- */
/* Array Helpers                                                              */
/* -------------------------------------------------------------------------- */

function isItemOption<TValue extends SelectValueType = string>(
  item?: TValue | BaseOption<TValue>,
): item is BaseOption<TValue> {
  return Boolean(item) && Boolean((item as BaseOption<TValue>).value);
}

function getValue<TValue extends SelectValueType = string>(
  item?: TValue | BaseOption<TValue>,
): TValue | undefined {
  return isItemOption(item) ? item.value : item;
}

// Making these `any` since we don't care what the type of value is
export const sortByIndex = (a: BaseOption<any>, b: BaseOption<any>) => {
  const aIndex = a.sortIndex ?? 0;
  const bIndex = b.sortIndex ?? 0;
  return aIndex - bIndex;
};

export function convertMapToArray<TValue = any, TKey = string>(map: Map<TKey, TValue>): TValue[] {
  const values = map?.values();
  return values ? [...values] : [];
}

export function getKeysInMap<TKey = string>(map: Map<TKey, any>): TKey[] {
  const keys = map?.keys();
  return keys ? [...keys] : [];
}

export const getValuesInMap = convertMapToArray;

/* ------------------------------------------------------------------------ */
/* Data Parsing                                                             */
/* ------------------------------------------------------------------------ */

/**
 *  If using a paginated api to get the values, the selected item may not be
 * the available options.  If this is the case, we need to build a fakey
 * option object
 */
export function createSelectedOption<TValue extends SelectValueType = string>(
  value: TValue,
): SelectableOption<TValue> {
  return {
    id: `${value}`,
    value,
    label: `${value}`,
    categoryId: DEFAULT_CATEGORY_ID,
    isSelected: true,
  };
}

/**
 * Does a full build of the state used by the `useCategorySelection` hook
 *
 * NOTE: `selectedOptions` needs to be a seperate array, as if you are using a
 * paginated api, you have have selected items which are not in the list of
 * options at this limit and offset
 *
 * @returns
 * {
 *   options: [], // available options
 *   selectedOptions: [], // all the options that are selected
 * }
 */
export function buildUseCategoryState<TValue extends SelectValueType = string>(
  defaultSelections: TValue[] = [],
  options: BaseOption<TValue>[] = [],
  { selectedOptions = new Map() }: UseCategorySelectionState<TValue> = {
    options: [],
    selectedOptions: new Map(),
  },
): UseCategorySelectionState<TValue> {
  const updatedOptions = options.map((o, i) => {
    const isSelected = defaultSelections.includes(o.value);
    // Object.assign preferred over spread syntax on "option" as its drastically more performant
    const option = Object.assign({}, o, {
      isSelected,
      sortIndex: i, // track the sort order so that we can stitch things back together
    });

    if (isSelected) {
      selectedOptions.set(o.value, option);
    } else if (selectedOptions.has(o.value)) {
      selectedOptions.delete(o.value);
    }

    return option;
  });

  // Add missing selections to map
  defaultSelections.forEach((s) => {
    const val = getValue(s);
    if (val && !selectedOptions.has(val)) {
      selectedOptions.set(val, createSelectedOption(val));
    }
  });
  return {
    options: updatedOptions,
    selectedOptions,
  };
}

// Helper function - Extracts the inner text from a ReactNode
export const getNodeText = (node: ReactNode): string =>
  React.isValidElement(node) ? node.props.children.toLocaleString() : (node ?? '').toLocaleString();
